import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('coach'); // 'coach' or 'client'

  // Check for session expired flag
  useEffect(() => {
    const sessionExpired = localStorage.getItem('session_expired');
    if (sessionExpired) {
      toast.info('Your session has expired. Please log in again.');
      localStorage.removeItem('session_expired');
    }
  }, []);

  const handleSubmit = async (e, roleOverride) => {
    e.preventDefault();
    setLoading(true);

    const loginRole = roleOverride || selectedRole;

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      const { access_token, user } = response.data;
      
      // Verify the logged-in user matches the selected role
      if (user.role !== loginRole) {
        toast.error(`This account is not a ${loginRole}. Please use the correct login option.`);
        setLoading(false);
        return;
      }
      
      onLogin(user, access_token);
      toast.success(`${loginRole === 'coach' ? 'Coach' : 'Client'} login successful!`);
      
      // Redirect to appropriate portal
      if (user.role === 'coach') {
        navigate('/coach');
      } else {
        navigate('/app');
      }
    } catch (error) {
      // Handle different error response formats
      let errorMessage = 'Login failed';
      const errorData = error.response?.data;
      
      if (errorData) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors come as an array
          errorMessage = errorData.detail.map(e => e.msg || e.message || 'Validation error').join(', ');
        } else if (typeof errorData.detail === 'object' && errorData.detail?.msg) {
          errorMessage = errorData.detail.msg;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="w-full max-w-md">
        <a 
          href="/" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to homepage
        </a>
        <Card className="w-full" data-testid="login-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <a href="/" className="cursor-pointer hover:opacity-80 transition-opacity">
                <img src="/logo.png" alt="MealGuide" className="h-24" />
              </a>
            </div>
            <CardDescription className="text-base">Coach Login</CardDescription>
          </CardHeader>
        <CardContent>
          <form onSubmit={(e) => handleSubmit(e, 'coach')} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="coach@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#1a61e8] hover:bg-[#1552c7]"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <div className="text-center mt-4 space-y-2">
            <button
              type="button"
              onClick={() => navigate('/app/forgot-password')}
              className="text-sm text-[#1a61e8] hover:underline"
              data-testid="coach-forgot-password-link"
            >
              Forgot your password?
            </button>
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={() => window.location.href = '/signup'}
                className="text-[#1a61e8] font-medium hover:underline"
              >
                Sign up
              </button>
            </p>
          </div>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
